import { isDefined } from "@clipboard-health/util-ts";
import { type BreakPolicyRule } from "@src/appV2/FacilityProfile/api/useGetFacilityProfile";

const fallbackTu: TuFunction = (defaultText) => defaultText;
import { type TuFunction } from "@src/appV2/i18n/types";

export function sortBreakPolicyRules(rules: BreakPolicyRule[]): BreakPolicyRule[] {
  return [...rules].sort((a, b) => a.minShiftDurationInMinutes - b.minShiftDurationInMinutes);
}

export type BreakPolicyRuleValidationResult =
  | { ok: true; rules: BreakPolicyRule[] }
  | { ok: false; message: string };

/**
 * Validates a list of draft break policy rules. Returns the finalized,
 * shift-length sorted rule list on success, or the first user-facing error
 * message on failure. Ordering by shift length is enforced by sorting, not by
 * rejecting unsorted input. Within that order, breaks must be non-decreasing.
 */
export function validateBreakPolicyRules(
  inputRules: Array<Partial<BreakPolicyRule>>,
  tu: TuFunction = fallbackTu
): BreakPolicyRuleValidationResult {
  const rules: BreakPolicyRule[] = [];
  for (const rule of inputRules) {
    if (!isDefined(rule.minShiftDurationInMinutes) || !isDefined(rule.breakDurationInMinutes)) {
      return { ok: false, message: tu("Please fill in both break rule fields") };
    }
    rules.push({
      minShiftDurationInMinutes: rule.minShiftDurationInMinutes,
      breakDurationInMinutes: rule.breakDurationInMinutes,
    });
  }

  const uniqueShiftDurations = new Set(rules.map((rule) => rule.minShiftDurationInMinutes));
  if (uniqueShiftDurations.size !== rules.length) {
    return { ok: false, message: tu("Each rule must have a unique shift length.") };
  }

  const uniqueBreakDurations = new Set(rules.map((rule) => rule.breakDurationInMinutes));
  if (uniqueBreakDurations.size !== rules.length) {
    return { ok: false, message: tu("Each rule must have a unique break length.") };
  }

  const sorted = sortBreakPolicyRules(rules);
  for (let index = 1; index < sorted.length; index += 1) {
    const previous = sorted[index - 1];
    const current = sorted[index];
    if (current.breakDurationInMinutes < previous.breakDurationInMinutes) {
      const currentHours = current.minShiftDurationInMinutes / 60;
      const previousHours = previous.minShiftDurationInMinutes / 60;
      return {
        ok: false,
        message: `The break duration for ${currentHours}-hour shifts has to be longer than for ${previousHours}-hour shifts.`,
      };
    }
  }

  return { ok: true, rules: sorted };
}
